<template>
    <Head title="Mes colis" />

    <FrontLayout>
        <div class="w-full text-xl text-gray-800 leading-normal mb-6">
            <h1 class="font-bold font-sans break-normal text-gray-900 pt-6 text-3xl md:text-4xl">
                Mes colis
            </h1>
            <p class="text-sm font-normal text-gray-600 flex items-center">
                <svg class="w5 h-5 fill-current mr-2" viewBox="0 0 24 24">
                    <path class="text-red-300" d="M12 10l12 7-12 7-12-7z"></path>
                    <path class="text-red-500" d="M12 5l12 7-12 7-12-7z"></path>
                    <path class="text-red-700" d="M12 0l12 7-12 7L0 7z"></path>
                </svg>
                Tableau récapitulatif des colis envoyé avec Boti Cargo Shipping.
            </p>
        </div>
        <div x-data="handleSelect mb-6">
            <Datatables>
                <template #title>
                    Liste de mes colis
                    <span class="font-medium">({{ packages.total }})</span>
                </template>

                <template #headerButtons>
                    <div class="grid grid-flow-col sm:auto-cols-max sm:justify-end gap-2">
                        <button @click="showSearchModal" class="rounded-md py-0 px-2 bg-white text-sky hover:bg-sky-400 hover:text-white">
                            <i  class="fad fa-search cursor-pointer"></i>
                        </button>
                    </div>
                </template>
                <template #tableHeader>
                    <th>#</th>
                    <th>Code</th>
                    <th>Destinataire</th>
                    <th>Nombre de colis</th>
                    <th>Montant</th>
                    <th>Facturation</th>
                    <th>Statut</th>
                    <th class="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <span class="sr-only">Menu</span>
                    </th>
                </template>
                <template #tableBody>
                    <template v-for="(pack, index) in packages.data" :key="pack.id">
                        <tr>
                            <td class="whitespace-nowrap">{{ index + 1 }}</td>
                            <td class="whitespace-nowrap">
                                <span class="font-bold text-sky">{{ pack.code }}</span>
                            </td>
                            <td class="whitespace-nowrap">
                                <div class="grow text-left">
                                    <h2 class="font-semibold text-gray-500 inline-flex">{{ pack.receiver_name ?? 'Non renseigné' }}</h2>
                                    <div class="text-xs text-gray-400">{{ pack.receiver_phone ? '+' + pack.receiver_dial_code + ' ' + pack.receiver_phone : 'Non renseigné' }}</div>
                                </div>
                            </td>
                            <td class="whitespace-nowrap">
                                {{ pack.weight ? pack.weight : 'Non défini' }}
                            </td>
                            <td class="whitespace-nowrap">
                                <div class="text-right font-bold" :class="'text-'+pack.status_color+'-500'">
                                    {{ formatAmount(pack.amount, currency) }}
                                </div>
                            </td>
                            <td class="whitespace-nowrap">
                                <div class="inline-flex font-medium rounded-full text-center px-2.5 py-0.5"
                                    :class="'bg-'+pack.invoice.status_color+'-100 text-'+pack.invoice.status_color+'-500 border border-'+pack.invoice.status_color+'-500'"
                                >{{ pack.invoice.status_text }}
                                </div>
                            </td>
                            <td class="whitespace-nowrap">
                                <div class="inline-flex font-medium rounded-full text-center px-2.5 py-0.5"
                                    :class="'bg-'+pack.status_color+'-100 text-'+pack.status_color+'-500 border border-'+pack.status_color+'-500'"
                                >{{ pack.status_text }}
                                </div>
                            </td>
                            <td class="whitespace-nowrap">
                                <div class="flex justify-end">
                                    <i @click.prevent="openDetails(pack)" aria-controls="description-02"
                                        class="fad fa-arrow-down text-sm text-sky hover:text-sky-400 transform
                                        cursor-pointer transition-transform duration-200 ease-in-out"
                                        :class="{ 'rotate-180' : selectedPackage && selectedPackage.id === pack.id }"
                                        :title="selectedPackage && selectedPackage.id === pack.id ? 'Cacher' : 'Détails'"
                                    ></i>
                                </div>
                            </td>
                        </tr>
                        <tr id="description-02" role="region" v-show="selectedPackage && selectedPackage.id === pack.id">
                            <td colspan="8">
                                <div class="text-left text-sm px-4 text-gray-700">
                                    <span class="font-bold ">Description du colis:</span> {{ pack.description ?? 'Non renseigné' }}
                                </div>
                                <hr class="my-2">
                                <div class="text-left text-sm px-4 text-gray-700">
                                    <span class="font-bold ">Adresse de récupération du colis:</span> {{ pack.address ?? 'Non renseigné' }}
                                </div>
                                <div class="p-4 bg-gray-100">
                                    <h2 class="bg-gray-200 text-gray-800 font-semibold px-2 py-1 text-left">
                                        Historique des paiements
                                    </h2>
                                    <div class="overflow-x-auto">
                                        <table class="w-full">
                                            <thead class="text-xs font-semibold uppercase text-gray-500 bg-gray-50 border-t border-b border-gray-200">
                                                <tr>
                                                    <th>Référence</th>
                                                    <th>Montant</th>
                                                    <th>Mode de paiement</th>
                                                    <th>Date</th>
                                                </tr>
                                            </thead>
                                            <tbody class="text-xs divide-y divide-gray-200">
                                                <template v-if="!pack.invoice.payments.length">
                                                    <tr>
                                                        <td colspan="7">
                                                            <div class="text-center text-lg text-red-400 bg-red-100 p-4">
                                                                Vous n'avez encore effectué aucun paiement sur ce colis.
                                                            </div>
                                                        </td>
                                                    </tr>
                                                </template>
                                                <template v-else>
                                                    <tr v-for="(payment, index) in pack.invoice.payments" :key="index">
                                                        <td>
                                                            <span class="text-sky font-semibold">{{ payment.reference }}</span>
                                                        </td>
                                                        <td>
                                                            <div class="text-right">{{ formatAmount(payment.amount, currency) }}</div>
                                                        </td>
                                                        <td>{{ payment.mode_real_text }}</td>
                                                        <td class="capitalize">{{ formattedDate(payment.created_at) }}</td>
                                                    </tr>
                                                </template>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </template>
                </template>
            </Datatables>
        </div>

        <Pagination :links="packages.links" />

        <JetDialogModal :show="searchModal" @close="closeSearchModal" max-width="xl">
            <template #title>Rechercher un colis</template>
            <template #content>
                <GlobalFilter @search-launched="closeSearchModal" />
            </template>
        </JetDialogModal>
    </FrontLayout>
</template>

<script>
    import { Head } from '@inertiajs/inertia-vue3';
    import Datatables from '@/Components/Datatables'
    import FrontLayout from '@/Layouts/FrontLayout.vue'
    import CurrencyMixins from '@/Mixins/CurrencyMixins'
    import JetDialogModal from '@/Jetstream/DialogModal'
    import GlobalFilter from '@/Components/GlobalFilter'
    import DateFormatMixins from '@/Mixins/DateFormatMixins'
    import SearchActionMixins from '@/Mixins/SearchActionMixins'

    export default {
        components: {
            Head,
            Datatables,
            FrontLayout,
            GlobalFilter,
            JetDialogModal
        },

        mixins: [CurrencyMixins, SearchActionMixins, DateFormatMixins],

        props: {
            currency: Object,
            packages: Object
        },

        data() {
            return {
                selectedPackage: null
            }
        },

        methods:{
            openDetails(req) {
                if (this.selectedPackage && this.selectedPackage.id === req.id) {
                    this.selectedPackage = null;
                } else {
                    this.selectedPackage = req
                }
            },
        }
    }
</script>
